import React, { useEffect, Fragment, memo } from 'react';
import { Box, Flex, Grid, VStack, Text, Button } from '@chakra-ui/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
// ── Shared primitives ─────────────────────────────────────────────
function SectionLabel({
  number,
  title
}: {
  number: string;
  title: string;
}) {
  return <Box mb={10} data-id="element-3">
      <Text className="font-mono reveal" fontSize="11px" letterSpacing="0.15em" color="#6E6E73" textTransform="uppercase" mb={4} data-id="element-4">
        {number} / {title}
      </Text>
      <Box className="section-rule" data-id="element-5" />
    </Box>;
}
function ThinRule() {
  return <Box className="section-rule reveal" my={12} data-id="element-6" />;
}
function SignalBox({
  text
}: {
  text: string;
}) {
  return <Box bg="#F5F5F7" px={3} py={2} mt={3} borderRadius="8px" data-id="element-7">
      <Text className="font-mono" fontSize="11px" color="#86868B" fontStyle="italic" data-id="element-8">
        {text}
      </Text>
    </Box>;
}
function RiskTag({
  text,
  highlight = false
}: {
  text: string;
  highlight?: boolean;
}) {
  return <Box display="inline-block" bg={highlight ? '#0071E3' : '#F5F5F7'} px={3} py={1} mt={4} borderRadius="980px" data-id="element-9">
      <Text className="font-mono" fontSize="9px" letterSpacing="0.12em" color={highlight ? '#FFFFFF' : '#6E6E73'} textTransform="uppercase" data-id="element-10">
        {text}
      </Text>
    </Box>;
}
function AudienceBlock({
  label,
  text,
  highlight = false
}: {
  label: string;
  text: string;
  highlight?: boolean;
}) {
  return <Box flex="1" bg="#FFFFFF" borderTop={`2px solid ${highlight ? '#0071E3' : '#D2D2D7'}`} p={{
    base: 8,
    md: 10
  }} borderRadius="0 0 12px 12px" className="reveal" data-id="element-11">
      <Text className="font-mono" fontSize="10px" letterSpacing="0.2em" color="#86868B" textTransform="uppercase" mb={5} data-id="element-12">
        {label}
      </Text>
      <Text className="font-sans" fontSize="15px" color="#6E6E73" lineHeight="1.8" fontWeight="300" data-id="element-13">
        {text}
      </Text>
    </Box>;
}
// ── Section 00: LA PORTADA ────────────────────────────────────────
function Section00Cover() {
  return <Box as="section" minH="100vh" bg="#FFFFFF" display="flex" flexDirection="column" justifyContent="center" alignItems="center" textAlign="center" px={{
    base: 6,
    md: 16
  }} py={24} data-id="element-14">
      <VStack spacing={0} maxW="760px" w="full" data-id="element-15">
        <Text className="font-mono reveal" fontSize="11px" letterSpacing="0.25em" color="#86868B" textTransform="uppercase" mb={8} data-id="element-16">
          Gustavo Soto Miño
        </Text>

        <Text className="font-sans reveal" fontSize={{
        base: '13px',
        md: '14px'
      }} color="#86868B" lineHeight="1.6" maxW="440px" mb={10} fontWeight="300" fontStyle="italic" data-id="element-17">
          Para quienes implementaron IA y algo no cuadra.
          <br data-id="element-18" />Y para quienes están a punto de hacerlo.
        </Text>

        <Text className="font-serif-italic reveal" fontSize={{
        base: '52px',
        md: '80px',
        lg: '96px'
      }} lineHeight="1.0" color="#1D1D1F" letterSpacing="-0.02em" mb={8} data-id="element-19">
          La urgencia
          <br data-id="element-20" />
          de decidir.
        </Text>

        {/* Book cover image */}
        <Box className="reveal" mb={12} w={{
        base: '280px',
        md: '420px'
      }} mx="auto" data-id="element-21">
          <Box as="img" src="https://cdn.magicpatterns.com/uploads/381AqsevFTN5zMuF2yggQe/books-mockup-lying-on-a-white-surface-17401a.png" alt="La urgencia de decidir — portada" w="full" style={{
          objectFit: 'contain',
          filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.14))'
        }} data-id="element-22" />
        </Box>

        <Box className="section-rule reveal" mb={12} maxW="60px" mx="auto" data-id="element-23" />

        <Text className="font-serif reveal" fontSize={{
        base: '18px',
        md: '22px'
      }} color="#6E6E73" lineHeight="1.5" mb={2} data-id="element-24">
          El verdadero problema nunca fue producir.
        </Text>
        <Text className="font-serif reveal" fontSize={{
        base: '26px',
        md: '34px'
      }} color="#1D1D1F" lineHeight="1.3" mb={16} fontWeight="600" data-id="element-25">
          Fue decidir.
        </Text>

        <Box className="reveal" mb={16} data-id="element-26">
          <Button as="a" href="#section-01" bg="#0071E3" color="#FFFFFF" px={8} py={5} h="auto" fontSize="14px" fontFamily="'DM Sans', sans-serif" letterSpacing="0.01em" borderRadius="980px" fontWeight="400" _hover={{
          bg: '#0077ED'
        }} transition="all 0.2s ease" display="inline-flex" alignItems="center" data-id="element-27">
            Leer las ideas esenciales →
          </Button>
        </Box>

        <Box className="bounce-subtle" color="#C7C7CC" data-id="element-28">
          <svg width="20" height="28" viewBox="0 0 20 28" fill="none" data-id="element-29">
            <line x1="10" y1="0" x2="10" y2="20" stroke="#C7C7CC" strokeWidth="1" data-id="element-30" />
            <polyline points="4,14 10,22 16,14" fill="none" stroke="#C7C7CC" strokeWidth="1" data-id="element-31" />
          </svg>
        </Box>
      </VStack>
    </Box>;
}
// ── Section 01: EL MENSAJE ────────────────────────────────────────
function Section01Message() {
  return <Box as="section" id="section-01" bg="#F5F5F7" px={{
    base: 6,
    md: 12,
    lg: 20
  }} py={{
    base: 20,
    md: 36
  }} data-id="element-32">
      <Box maxW="1080px" mx="auto" data-id="element-33">
        <SectionLabel number="01" title="El mensaje" data-id="element-34" />
        <Box maxW="640px" mx="auto" textAlign="center" data-id="element-35">
          <Text className="font-serif-italic reveal" fontSize={{
          base: '26px',
          md: '40px'
        }} color="#1D1D1F" lineHeight="1.25" letterSpacing="-0.01em" mb={10} data-id="element-36">
            Hay una sensación que conoces bien.
            <br data-id="element-37" />
            Produces. Entregas. Mides.
            <br data-id="element-38" />Y algo debajo de todo eso no termina de moverse.
          </Text>
          <Text className="font-sans reveal" fontSize={{
          base: '15px',
          md: '17px'
        }} color="#6E6E73" lineHeight="1.8" fontWeight="300" mb={10} data-id="element-39">
            No es la herramienta. No es el equipo. No es la metodología. Es que
            el sistema lleva meses — a veces años — ejecutando decisiones que
            nadie declaró como decisiones. Decisiones sin dueño. Sin criterio
            explícito. Sin fecha de vencimiento. Y cuando nadie las toma, el
            sistema las toma igual. Por inercia.
          </Text>
          <ThinRule data-id="element-40" />
          <VStack spacing={8} align="center" mb={12} data-id="element-41">
            <Text className="font-mono reveal" fontSize="11px" letterSpacing="0.2em" color="#86868B" textTransform="uppercase" data-id="element-42">
              No es un problema de metodología.
            </Text>
            <Text className="font-sans reveal" fontSize={{
            base: '17px',
            md: '21px'
          }} color="#6E6E73" lineHeight="1.5" data-id="element-43">
              No es un problema de roles ni de tecnología.
            </Text>
            <Text className="font-serif-italic reveal" fontSize={{
            base: '22px',
            md: '32px'
          }} color="#1D1D1F" lineHeight="1.3" letterSpacing="-0.01em" data-id="element-44">
              Es un problema de decisiones que nadie declaró como decisiones.
            </Text>
          </VStack>
          <ThinRule data-id="element-45" />
        </Box>

        {/* Dual audience blocks */}
        <Flex direction={{
        base: 'column',
        md: 'row'
      }} gap={4} mb={12} data-id="element-46">
          <AudienceBlock label="Si ya implementaste IA" text="El problema que describes probablemente no es técnico. Es decisional. El sistema heredó criterios que nadie validó y los ejecutó a escala." highlight={false} data-id="element-47" />
          <AudienceBlock label="Si estás a punto de hacerlo" text="La velocidad que vas a ganar va a amplificar exactamente lo que no tengas resuelto. Antes de ejecutar, hay que decidir qué se decide." highlight={true} data-id="element-48" />
        </Flex>

        <Box maxW="640px" mx="auto" textAlign="center" data-id="element-49">
          <Text className="font-sans reveal" fontSize={{
          base: '15px',
          md: '17px'
        }} color="#6E6E73" lineHeight="1.8" fontWeight="300" mb={8} data-id="element-50">
            La inteligencia artificial no creó este problema. Lo heredó. Lo
            amplificó. Y lo hizo imposible de ignorar.
          </Text>
          <Text className="font-serif-italic reveal" fontSize={{
          base: '18px',
          md: '22px'
        }} color="#1D1D1F" lineHeight="1.5" data-id="element-51">
            Este libro fue escrito para nombrarlo.
          </Text>
        </Box>
      </Box>
    </Box>;
}
// ── Section 02: EL CONTEXTO ───────────────────────────────────────
function Section02Context() {
  return <Box as="section" bg="#FFFFFF" px={{
    base: 6,
    md: 12,
    lg: 20
  }} py={{
    base: 20,
    md: 36
  }} data-id="element-52">
      <Box maxW="1080px" mx="auto" data-id="element-53">
        <SectionLabel number="02" title="El contexto" data-id="element-54" />
        <Text className="font-serif-italic reveal" fontSize={{
        base: '28px',
        md: '44px'
      }} color="#1D1D1F" lineHeight="1.2" letterSpacing="-0.01em" maxW="760px" mb={4} data-id="element-55">
          Hubo un tiempo en que el sistema tenía un activo que nadie había
          declarado en ningún balance.
        </Text>
        <Text className="font-sans reveal" fontSize={{
        base: '17px',
        md: '21px'
      }} color="#6E6E73" lineHeight="1.5" mb={12} fontWeight="300" data-id="element-56">
          Se llamaba margen. Y desapareció sin hacer anuncio.
        </Text>
        <VStack spacing={6} align="flex-start" maxW="640px" mb={14} data-id="element-57">
          <Text className="font-sans reveal" fontSize={{
          base: '15px',
          md: '17px'
        }} color="#6E6E73" lineHeight="1.8" fontWeight="300" data-id="element-58">
            Margen de tiempo para corregir lo que se había construido mal.
            Margen de presupuesto para compensar las decisiones que no habían
            sido del todo pensadas. Margen de paciencia — de los usuarios, del
            mercado, de la organización — para aprender después del impacto,
            cuando ya era posible ver lo que la incertidumbre había ocultado
            durante la construcción.
          </Text>
          <Text className="font-sans reveal" fontSize={{
          base: '15px',
          md: '17px'
        }} color="#6E6E73" lineHeight="1.8" fontWeight="300" data-id="element-59">
            Ese margen hacía que muchas decisiones 'funcionaran' sin ser buenas.
            No porque fueran correctas, sino porque el sistema tenía colchones
            suficientes para absorber sus consecuencias antes de que se
            volvieran inapelables.
          </Text>
        </VStack>
        <ThinRule data-id="element-60" />

        {/* Three tension cards */}
        <Grid templateColumns={{
        base: '1fr',
        md: 'repeat(3, 1fr)'
      }} gap={4} mb={14} data-id="element-61">
          {[{
          tag: 'Tensión 01 — Usuarios',
          title: 'Expectativas que no vuelven atrás',
          body: 'Los usuarios entrenados por los mejores productos digitales del mundo aplican ese estándar en todas partes — banca, seguros, retail, salud, logística. La brecha entre lo que esperan y lo que encuentran ya no es un problema de interfaz. Es un problema de criterio sobre qué se decide construir y para quién.'
        }, {
          tag: 'Tensión 02 — Mercado',
          title: 'El capital que dejó de ser paciente',
          body: 'Durante años, el capital financiaba la incertidumbre porque el potencial parecía mayor que el riesgo. Ese cálculo cambió. El margen de ambigüedad se cerró. Las decisiones postergadas — las que nadie quería tomar en las reuniones de producto — aparecen ahora en el balance. Y tienen costo visible.'
        }, {
          tag: 'Tensión 03 — Tecnología',
          title: 'Ejecución barata, error caro',
          body: 'La IA comprimió ciclos completos. Definición, diseño, construcción, validación y despliegue pueden ocurrir en paralelo y a una velocidad que ninguna organización estaba preparada para gobernar. El problema ya no es llegar tarde. Es llegar rápido con una mala decisión.'
        }].map((card, i) => <Box key={i} className={`apple-card-gray card-hover reveal reveal-delay-${i}`} p={{
          base: 8,
          md: 10
        }} data-id="element-62">
              <Text className="font-mono" fontSize="10px" color="#86868B" letterSpacing="0.15em" textTransform="uppercase" mb={4} data-id="element-63">
                {card.tag}
              </Text>
              <Text className="font-serif-italic" fontSize={{
            base: '20px',
            md: '24px'
          }} color="#1D1D1F" lineHeight="1.3" mb={5} data-id="element-64">
                {card.title}
              </Text>
              <Box w="24px" h="1px" bg="#D2D2D7" mb={5} data-id="element-65" />
              <Text className="font-sans" fontSize="14px" color="#6E6E73" lineHeight="1.7" fontWeight="300" data-id="element-66">
                {card.body}
              </Text>
            </Box>)}
        </Grid>

        <Box className="reveal" textAlign="center" py={4} mb={12} data-id="element-67">
          <Text className="font-mono" fontSize="10px" letterSpacing="0.2em" color="#86868B" textTransform="uppercase" data-id="element-68">
            Las tres fuerzas no llegan en secuencia. Se superponen. Se
            amplifican entre sí.
          </Text>
        </Box>
        <Box textAlign="center" className="reveal" data-id="element-69">
          <Text className="font-serif-italic" fontSize={{
          base: '22px',
          md: '34px'
        }} color="#1D1D1F" lineHeight="1.35" letterSpacing="-0.01em" data-id="element-70">
            El margen que absorbía tus errores estratégicos desapareció.
            <br data-id="element-71" />Y nadie hizo el anuncio.
          </Text>
        </Box>
      </Box>
    </Box>;
}
// ── Section 03: LA EVIDENCIA ──────────────────────────────────────
function Section03Evidence() {
  return <Box as="section" bg="#F5F5F7" px={{
    base: 6,
    md: 12,
    lg: 20
  }} py={{
    base: 20,
    md: 36
  }} data-id="element-72">
      <Box maxW="1080px" mx="auto" data-id="element-73">
        <SectionLabel number="03" title="La evidencia" data-id="element-74" />

        <Text className="font-sans reveal" fontSize={{
        base: '15px',
        md: '17px'
      }} color="#6E6E73" lineHeight="1.8" fontWeight="300" maxW="760px" mb={12} data-id="element-75">
          Esto no ocurre en empresas que no saben lo que hacen. Ocurre en
          equipos competentes, con herramientas buenas, trabajando con esfuerzo
          real. El problema no es la capacidad. Es lo que el sistema decide
          cuando nadie está mirando.
        </Text>

        <ThinRule data-id="element-76" />

        <Grid templateColumns={{
        base: '1fr',
        lg: '1fr 1fr'
      }} gap={{
        base: 12,
        lg: 16
      }} mb={16} alignItems="start" data-id="element-77">
          <Box data-id="element-78">
            <Text className="font-serif-italic reveal" fontSize={{
            base: '26px',
            md: '38px'
          }} color="#1D1D1F" lineHeight="1.25" mb={10} letterSpacing="-0.01em" data-id="element-79">
              Nadie en esa sala sabía quién había tomado esa decisión.
            </Text>
            <VStack spacing={6} align="flex-start" data-id="element-80">
              <Text className="font-sans reveal" fontSize={{
              base: '15px',
              md: '17px'
            }} color="#6E6E73" lineHeight="1.8" fontWeight="300" data-id="element-81">
                Un proceso requería cuatro firmas para aprobarse. Nadie sabía
                por qué cuatro. En algún momento, alguien había agregado una
                firma adicional — probablemente con una razón válida en ese
                momento — y se quedó. El proceso había sido diseñado para un
                equipo de veinte personas. Hoy lo usaban quinientas.
              </Text>
              <Text className="font-sans reveal" fontSize={{
              base: '15px',
              md: '17px'
            }} color="#6E6E73" lineHeight="1.8" fontWeight="300" data-id="element-82">
                Y cada una de esas quinientas personas asumía que las otras
                cuatro firmas tenían sentido.
              </Text>
              <Text className="font-sans reveal" fontSize={{
              base: '15px',
              md: '17px'
            }} color="#6E6E73" lineHeight="1.8" fontWeight="300" data-id="element-83">
                Cuando alguien finalmente preguntó por qué — la respuesta no fue
                técnica ni normativa. Fue esta:{' '}
                <Box as="span" color="#1D1D1F" data-id="element-84">
                  'porque así siempre se hizo'.
                </Box>{' '}
                Nadie recordaba el origen. Nadie había documentado el criterio.
                La decisión no tenía dueño ni fecha de vencimiento. Simplemente
                estaba ahí, ralentizando el sistema con la autoridad silenciosa
                de lo que nunca fue cuestionado.
              </Text>
            </VStack>
          </Box>

          <Box className="reveal reveal-delay-1" data-id="element-85">
            <svg viewBox="0 0 360 220" style={{
            width: '100%',
            height: 'auto',
            maxWidth: '400px'
          }} data-id="element-86">
              <defs data-id="element-87">
                <marker id="arrowEvidence" markerWidth="6" markerHeight="5" refX="5" refY="2.5" orient="auto" data-id="element-88">
                  <polygon points="0 0, 6 2.5, 0 5" fill="#C7C7CC" data-id="element-89" />
                </marker>
                <marker id="arrowApproval" markerWidth="6" markerHeight="5" refX="5" refY="2.5" orient="auto" data-id="element-90">
                  <polygon points="0 0, 6 2.5, 0 5" fill="#C7C7CC" data-id="element-91" />
                </marker>
              </defs>
              {/* Four signature nodes in series */}
              {['Firma 01', 'Firma 02', 'Firma 03', 'Firma 04'].map((label, i) => <g key={i} data-id="element-92">
                    <rect x={10 + i * 84} y={20} width={68} height={44} rx="4" fill="#FFFFFF" stroke="#D2D2D7" strokeWidth="1" data-id="element-93" />
                    <text x={10 + i * 84 + 34} y={38} textAnchor="middle" fill="#86868B" fontSize="8" fontFamily="'Courier New', monospace" letterSpacing="0.5" data-id="element-94">
                      {label.split(' ')[0]}
                    </text>
                    <text x={10 + i * 84 + 34} y={52} textAnchor="middle" fill="#1D1D1F" fontSize="10" fontFamily="'Courier New', monospace" fontWeight="700" data-id="element-95">
                      {label.split(' ')[1]}
                    </text>
                    {i < 3 && <line x1={10 + i * 84 + 68} y1={42} x2={10 + (i + 1) * 84} y2={42} stroke="#C7C7CC" strokeWidth="1" markerEnd="url(#arrowEvidence)" data-id="element-96" />}
                  </g>)}
              {/* Approval time arrow */}
              <line x1={10} y1={90} x2={330} y2={90} stroke="#C7C7CC" strokeWidth="1" markerEnd="url(#arrowApproval)" data-id="element-97" />
              <text x={170} y={106} textAnchor="middle" fill="#86868B" fontSize="10" fontFamily="'DM Sans', sans-serif" data-id="element-98">
                tiempo de aprobación
              </text>
              {/* Dotted line down from end */}
              <line x1={330} y1={90} x2={330} y2={160} stroke="#C7C7CC" strokeWidth="1" strokeDasharray="4,3" data-id="element-99" />
              <text x={330} y={178} textAnchor="middle" fill="#86868B" fontSize="10" fontFamily="'DM Sans', sans-serif" fontStyle="italic" data-id="element-100">
                ¿quién decidió
              </text>
              <text x={330} y={193} textAnchor="middle" fill="#86868B" fontSize="10" fontFamily="'DM Sans', sans-serif" fontStyle="italic" data-id="element-101">
                que fueran cuatro?
              </text>
            </svg>
          </Box>
        </Grid>

        <Box className="apple-card reveal" p={{
        base: 8,
        md: 12
      }} mb={12} maxW="440px" data-id="element-102">
          <Text className="font-serif" fontSize={{
          base: '80px',
          md: '104px'
        }} color="#1D1D1F" lineHeight="1" mb={3} fontWeight="700" data-id="element-103">
            0
          </Text>
          <Text className="font-sans" fontSize="14px" color="#6E6E73" lineHeight="1.6" fontWeight="300" data-id="element-104">
            personas en esa sala sabían por qué el proceso requería exactamente
            ese número de aprobaciones
          </Text>
        </Box>

        <Box className="pull-quote-border reveal" maxW="680px" mb={14} data-id="element-105">
          <Text className="font-serif-italic" fontSize={{
          base: '17px',
          md: '22px'
        }} color="#1D1D1F" lineHeight="1.6" data-id="element-106">
            "Cuando un sistema decide así — silencioso, por inercia, sin que
            nadie lo declare como decisión — la mejora se convierte en
            maquillaje. Puedes digitalizar el flujo, agregar notificaciones,
            construir un dashboard. El problema sigue donde siempre estuvo: en
            una decisión que nadie tomó pero que todos obedecen."
          </Text>
        </Box>

        <ThinRule data-id="element-107" />

        <Box mb={10} data-id="element-108">
          <Text className="font-mono reveal" fontSize="11px" letterSpacing="0.2em" color="#6E6E73" textTransform="uppercase" mb={3} data-id="element-109">
            Los cuatro tipos de decisiones invisibles
          </Text>
          <Text className="font-serif-italic reveal" fontSize={{
          base: '15px',
          md: '17px'
        }} color="#86868B" mb={10} data-id="element-110">
            Toda organización las tiene. La mayoría no sabe nombrarlas.
          </Text>
        </Box>

        {/* 2×2 Grid */}
        <Grid templateColumns={{
        base: '1fr',
        md: 'repeat(2, 1fr)'
      }} gap={4} mb={10} data-id="element-111">
          {[{
          num: '01',
          title: 'Heredadas',
          desc: 'Alguien las tomó en otro momento. El sistema las adoptó como norma sin ratificarlas. Operan como si fueran arquitectura inamovible cuando en realidad son elecciones que el tiempo volvió invisibles.',
          signal: "Lo escucharás así: 'siempre fue así'",
          tag: 'Riesgo bajo · Frecuencia alta',
          highlight: false
        }, {
          num: '02',
          title: 'Diferidas',
          desc: 'Nadie las tomó del todo. El sistema avanzó con supuestos provisionales que se volvieron permanentes. La decisión sigue pendiente — solo que pendiente se convirtió en el estado por defecto.',
          signal: "Lo escucharás así: 'eso nunca quedó del todo definido'",
          tag: 'Riesgo medio · Silenciosas',
          highlight: false
        }, {
          num: '03',
          title: 'Fragmentadas',
          desc: 'Cada función decidió su tramo. Nadie gobernó el recorrido completo. El resultado no fue diseñado — emergió de la suma de decisiones localmente racionales pero globalmente incoherentes.',
          signal: "Lo escucharás así: 'cada equipo hizo lo suyo'",
          tag: 'Riesgo medio · Difícil de trazar',
          highlight: false
        }, {
          num: '04',
          title: 'Automatizadas',
          desc: 'Están codificadas en reglas, prompts o modelos. El sistema las ejecuta. Nadie las revisa. A escala. De forma continua. En silencio. Esta es la categoría más urgente porque es la única que escala sin fricción humana que frene el daño.',
          signal: "Lo escucharás así: 'el modelo lo decidió'",
          tag: 'Riesgo alto · Las más urgentes',
          highlight: true
        }].map((q, i) => <Box key={i} className={`apple-card card-hover reveal reveal-delay-${i}`} p={{
          base: 8,
          md: 10
        }} border={q.highlight ? '1px solid #0071E3' : undefined} data-id="element-112">
              <Box w="28px" h="28px" borderRadius="full" bg={q.highlight ? '#0071E3' : '#1D1D1F'} display="flex" alignItems="center" justifyContent="center" mb={5} data-id="element-113">
                <Text className="font-mono" fontSize="10px" color="#FFFFFF" fontWeight="700" data-id="element-114">
                  {q.num}
                </Text>
              </Box>
              <Text className="font-mono" fontSize="12px" color={q.highlight ? '#0071E3' : '#1D1D1F'} letterSpacing="0.12em" textTransform="uppercase" mb={4} fontWeight="600" data-id="element-115">
                {q.title}
              </Text>
              <Text className="font-serif-italic" fontSize={{
            base: '13px',
            md: '14px'
          }} color="#6E6E73" lineHeight="1.7" mb={4} data-id="element-116">
                {q.desc}
              </Text>
              <SignalBox text={q.signal} data-id="element-117" />
              <RiskTag text={q.tag} highlight={q.highlight} data-id="element-118" />
            </Box>)}
        </Grid>

        <Text className="font-sans reveal" fontSize={{
        base: '14px',
        md: '16px'
      }} color="#6E6E73" lineHeight="1.7" fontWeight="300" maxW="640px" data-id="element-119">
          No se puede gobernar lo que no se puede ver.
          <br data-id="element-120" />
          Reconocer qué tipo de decisión invisible enfrentas es la primera
          intervención.
        </Text>
      </Box>
    </Box>;
}
// ── Section 04: LA URGENCIA ───────────────────────────────────────
function Section04Urgency() {
  return <Box as="section" bg="#FFFFFF" px={{
    base: 6,
    md: 12,
    lg: 20
  }} py={{
    base: 20,
    md: 36
  }} data-id="element-121">
      <Box maxW="1080px" mx="auto" data-id="element-122">
        <SectionLabel number="04" title="La urgencia" data-id="element-123" />

        <Box textAlign="center" mb={14} data-id="element-124">
          <Text className="font-serif-italic reveal" fontSize={{
          base: '28px',
          md: '48px'
        }} color="#1D1D1F" lineHeight="1.2" letterSpacing="-0.01em" maxW="760px" mx="auto" data-id="element-125">
            Esto no es nuevo.
            <br data-id="element-126" />
            Lo nuevo es que ya no hay margen para ignorarlo.
          </Text>
        </Box>

        <Text className="font-sans reveal" fontSize={{
        base: '15px',
        md: '17px'
      }} color="#6E6E73" lineHeight="1.8" fontWeight="300" maxW="600px" mb={8} data-id="element-127">
          Durante años, estos costos fueron tolerables porque el contexto los
          absorbía. Había tiempo suficiente para corregir, presupuesto
          suficiente para compensar, margen suficiente para que la ambigüedad no
          tuviera consecuencias inmediatas. Lo que era ineficiente era también
          invisible porque el sistema tenía colchones que lo amortiguaban.
        </Text>

        <Text className="font-serif reveal" fontSize={{
        base: '20px',
        md: '26px'
      }} color="#1D1D1F" lineHeight="1.4" mb={12} data-id="element-128">
          Esos colchones desaparecieron.
        </Text>

        <ThinRule data-id="element-129" />

        <VStack spacing={10} align="flex-start" mb={16} maxW="720px" data-id="element-130">
          <Text className="font-sans reveal" fontSize={{
          base: '14px',
          md: '16px'
        }} color="#86868B" lineHeight="1.6" fontWeight="300" data-id="element-131">
            La IA no pregunta si el criterio es correcto. Ejecuta.
          </Text>
          <Text className="font-sans reveal" fontSize={{
          base: '18px',
          md: '22px'
        }} color="#6E6E73" lineHeight="1.5" fontWeight="300" data-id="element-132">
            Una mala decisión tomada una vez ahora escala con la consistencia de
            una buena.
          </Text>
          <Text className="font-serif-italic reveal" fontSize={{
          base: '24px',
          md: '36px'
        }} color="#1D1D1F" lineHeight="1.3" letterSpacing="-0.01em" data-id="element-133">
            El sistema que antes no decidía del todo ahora decide. A velocidad.
            A escala. En silencio.
          </Text>
        </VStack>

        {/* Taleb box */}
        <Box className="apple-card-gray reveal" p={{
        base: 8,
        md: 10
      }} mb={16} maxW="720px" borderLeft="3px solid #0071E3" borderRadius="0 12px 12px 0" data-id="element-134">
          <Text className="font-sans" fontSize={{
          base: '14px',
          md: '16px'
        }} color="#6E6E73" lineHeight="1.8" fontWeight="300" data-id="element-135">
            Taleb llamó a esto fragilidad: la propiedad de los sistemas que,
            bajo presión, no solo fallan sino que fallan más de lo esperado. Una
            decisión mal formulada ya no es un problema local que un equipo
            resolverá en el siguiente sprint. Es una instrucción que el sistema
            sigue con fidelidad implacable. Y la IA, lejos de resolver eso, lo
            ejecuta a escala industrial.
          </Text>
        </Box>

        {/* Dual audience blocks */}
        <Flex direction={{
        base: 'column',
        md: 'row'
      }} gap={4} mb={16} data-id="element-136">
          <AudienceBlock label="Si ya implementaste IA" text="El problema que estás viendo no va a resolverse con un ajuste técnico. El modelo ejecuta bien. El criterio que ejecuta es el que nadie validó. Eso requiere una intervención diferente." highlight={false} data-id="element-137" />
          <AudienceBlock label="Si estás a punto de hacerlo" text="La velocidad que vas a ganar es real. El riesgo también. Todo lo que no tengas decidido explícitamente antes de escalar, el sistema lo va a decidir por ti. Con consistencia. Sin intención." highlight={true} data-id="element-138" />
        </Flex>

        <Box textAlign="center" className="reveal" maxW="760px" mx="auto" data-id="element-139">
          <Text className="font-serif-italic" fontSize={{
          base: '22px',
          md: '32px'
        }} color="#1D1D1F" lineHeight="1.4" letterSpacing="-0.01em" data-id="element-140">
            "La IA no pregunta si el criterio es correcto. Ejecuta. Y eso no va
            a cambiar."
          </Text>
        </Box>
      </Box>
    </Box>;
}
// ── Section 05: LOS CAMINOS ───────────────────────────────────────
function Section05Paths() {
  const spectrumPoints = [{
    label: 'Valores en conflicto',
    sub: 'Sin respuesta técnicamente correcta. Requiere juicio humano.',
    pos: '0%'
  }, {
    label: 'Criterio del sistema',
    sub: 'No puede delegarse lo que aún no está definido.',
    pos: '25%'
  }, {
    label: 'Validación de hipótesis',
    sub: 'El agente procesa — el criterio lo pone la persona.',
    pos: '50%'
  }, {
    label: 'Síntesis de información',
    sub: 'El agente puede decidir si el criterio está escrito.',
    pos: '75%'
  }, {
    label: 'Ejecución de flujos',
    sub: 'El agente ejecuta mejor si el proceso está definido.',
    pos: '100%'
  }];
  const cx = 220;
  const cy = 220;
  const r = 130;
  const nodeR = 52;
  const cycleNodes = [{
    angle: -90,
    label: 'NOMBRAR',
    num: '01',
    desc: 'Un agente de onboarding rechazó 4.200 solicitudes en tres semanas. El criterio que usaba no estaba mal técnicamente — estaba desactualizado. Nadie lo había nombrado como decisión.'
  }, {
    angle: 0,
    label: 'ASIGNAR',
    num: '02',
    desc: 'Un agente de cobranza contactó al mismo cliente ocho veces en cinco días. Ningún equipo era responsable de definir el límite. El sistema no tenía a quién preguntarle si eso estaba bien.'
  }, {
    angle: 90,
    label: 'EJECUTAR',
    num: '03',
    desc: 'Un agente de soporte comunicó información desactualizada durante semanas. El contenido había cambiado, pero nadie registró la actualización en el sistema que alimentaba al agente.'
  }, {
    angle: 180,
    label: 'REVISAR',
    num: '04',
    desc: 'Un agente de inversión aplicó el mismo criterio de riesgo durante seis meses sin que nadie lo revisara. El contexto del mercado había cambiado tres veces. El modelo seguía ejecutando la versión original.'
  }];
  const toRad = (d: number) => d * Math.PI / 180;
  const pts = cycleNodes.map(n => ({
    ...n,
    x: cx + r * Math.cos(toRad(n.angle)),
    y: cy + r * Math.sin(toRad(n.angle))
  }));
  const arcs = pts.map((pt, i) => {
    const next = pts[(i + 1) % pts.length];
    const mx = (pt.x + next.x) / 2 + (next.y - pt.y) * 0.18;
    const my = (pt.y + next.y) / 2 - (next.x - pt.x) * 0.18;
    const dx = next.x - pt.x;
    const dy = next.y - pt.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    return `M ${pt.x + dx / len * nodeR} ${pt.y + dy / len * nodeR} Q ${mx} ${my} ${next.x - dx / len * nodeR} ${next.y - dy / len * nodeR}`;
  });
  return <Box as="section" bg="#F5F5F7" px={{
    base: 6,
    md: 12,
    lg: 20
  }} py={{
    base: 20,
    md: 36
  }} data-id="element-141">
      <Box maxW="1080px" mx="auto" data-id="element-142">
        <SectionLabel number="05" title="Los caminos" data-id="element-143" />

        <Text className="font-serif-italic reveal" fontSize={{
        base: '26px',
        md: '42px'
      }} color="#1D1D1F" lineHeight="1.2" letterSpacing="-0.01em" maxW="680px" mb={8} data-id="element-144">
          Nombrar el problema no es suficiente. Lo que sigue son tres
          herramientas para empezar a gobernarlo.
        </Text>
        <Text className="font-sans reveal" fontSize={{
        base: '15px',
        md: '17px'
      }} color="#6E6E73" lineHeight="1.8" fontWeight="300" maxW="600px" mb={14} data-id="element-145">
          Gobernar las decisiones no significa documentar todo ni aprobar todo
          ni ralentizar el sistema. Significa hacer explícito lo que necesita
          serlo para que el sistema pueda aprender. No son frameworks teóricos.
          Son cosas que se pueden usar esta semana.
        </Text>

        <ThinRule data-id="element-146" />

        {/* TOOL 01 */}
        <Box mb={16} data-id="element-147">
          <Flex align="center" gap={3} mb={3} data-id="element-148">
            <Box w="28px" h="28px" borderRadius="full" bg="#0071E3" display="flex" alignItems="center" justifyContent="center" flexShrink={0} data-id="element-149">
              <Text className="font-mono" fontSize="10px" color="#FFFFFF" fontWeight="700" data-id="element-150">
                01
              </Text>
            </Box>
            <Text className="font-mono reveal" fontSize="12px" color="#1D1D1F" letterSpacing="0.15em" textTransform="uppercase" fontWeight="600" data-id="element-151">
              El Espectro de Delegación
            </Text>
          </Flex>
          <Text className="font-serif-italic reveal" fontSize={{
          base: '15px',
          md: '17px'
        }} color="#6E6E73" mb={6} data-id="element-152">
            ¿Qué decisiones puede tomar la IA — y cuáles no puede tomar sin
            criterio humano explícito?
          </Text>
          <Text className="font-sans reveal" fontSize={{
          base: '14px',
          md: '15px'
        }} color="#6E6E73" lineHeight="1.8" fontWeight="300" maxW="600px" mb={10} data-id="element-153">
            La pregunta no es qué puede hacer la IA técnicamente. Es qué puede
            decidir sin que el error sea irreversible. Esas son preguntas
            distintas. Y confundirlas es el origen de la mayoría de los
            problemas que aparecen después de la implementación.
          </Text>

          <Box className="reveal" mb={4} data-id="element-154">
            <Flex justify="space-between" mb={3} data-id="element-155">
              <Text className="font-mono" fontSize="9px" color="#86868B" letterSpacing="0.15em" textTransform="uppercase" data-id="element-156">
                Criterio humano irrenunciable
              </Text>
              <Text className="font-mono" fontSize="9px" color="#86868B" letterSpacing="0.15em" textTransform="uppercase" data-id="element-157">
                Delegable a agentes
              </Text>
            </Flex>
            <Box position="relative" data-id="element-158">
              <Box className="spectrum-bar" data-id="element-159" />
              <Box position="relative" h="80px" mt={0} data-id="element-160">
                {spectrumPoints.map((pt, i) => <Box key={i} position="absolute" left={pt.pos} top={0} transform="translateX(-50%)" textAlign="center" w="120px" data-id="element-161">
                    <Box w="1px" h="14px" bg="#C7C7CC" mx="auto" mb={1} data-id="element-162" />
                    <Text className="font-mono" fontSize="8px" color="#1D1D1F" letterSpacing="0.05em" lineHeight="1.3" whiteSpace="nowrap" data-id="element-163">
                      {pt.label}
                    </Text>
                    <Text className="font-sans" fontSize="8px" color="#86868B" lineHeight="1.3" mt={1} data-id="element-164">
                      {pt.sub}
                    </Text>
                  </Box>)}
              </Box>
            </Box>
          </Box>

          <Grid templateColumns={{
          base: '1fr',
          md: 'repeat(3, 1fr)'
        }} gap={4} mt={10} data-id="element-165">
            {[{
            n: '01',
            q: '¿Qué pasa si el agente falla?',
            a: 'Si la consecuencia es irreversible — una decisión sobre una persona, un contrato, una operación crítica — la decisión no puede delegarse sin supervisión humana activa. El agente ejecuta. Una persona responde.'
          }, {
            n: '02',
            q: '¿El criterio está completamente explícito?',
            a: 'Si no puedes escribirlo de una forma que no admita interpretación, el agente va a interpretarlo de todas formas. Solo que sin avisarte. Eso no es automatización. Es ambigüedad a escala.'
          }, {
            n: '03',
            q: '¿Quién responde si esto sale mal?',
            a: "Si la respuesta es 'el modelo lo decidió' — no hubo gobernanza. Hubo abdicación. La responsabilidad no se delega con la tarea. Se asigna antes de que la tarea empiece."
          }].map((item, i) => <Box key={i} className={`apple-card card-hover reveal reveal-delay-${i}`} p={{
            base: 6,
            md: 8
          }} data-id="element-166">
                <Box w="24px" h="24px" borderRadius="full" bg="#0071E3" display="flex" alignItems="center" justifyContent="center" mb={4} data-id="element-167">
                  <Text className="font-mono" fontSize="9px" color="#FFFFFF" fontWeight="700" data-id="element-168">
                    {item.n}
                  </Text>
                </Box>
                <Text className="font-serif-italic" fontSize={{
              base: '15px',
              md: '17px'
            }} color="#1D1D1F" lineHeight="1.4" mb={3} data-id="element-169">
                  {item.q}
                </Text>
                <Text className="font-sans" fontSize="13px" color="#6E6E73" lineHeight="1.6" fontWeight="300" data-id="element-170">
                  {item.a}
                </Text>
              </Box>)}
          </Grid>
        </Box>

        <ThinRule data-id="element-171" />

        {/* TOOL 02 */}
        <Box mb={16} data-id="element-172">
          <Flex align="center" gap={3} mb={3} data-id="element-173">
            <Box w="28px" h="28px" borderRadius="full" bg="#0071E3" display="flex" alignItems="center" justifyContent="center" flexShrink={0} data-id="element-174">
              <Text className="font-mono" fontSize="10px" color="#FFFFFF" fontWeight="700" data-id="element-175">
                02
              </Text>
            </Box>
            <Text className="font-mono reveal" fontSize="12px" color="#1D1D1F" letterSpacing="0.15em" textTransform="uppercase" fontWeight="600" data-id="element-176">
              El Ciclo de Gobierno
            </Text>
          </Flex>
          <Text className="font-serif-italic reveal" fontSize={{
          base: '15px',
          md: '17px'
        }} color="#6E6E73" mb={6} data-id="element-177">
            ¿Cómo convierte el sistema lo implícito en explícito — sin
            burocracia adicional?
          </Text>
          <Text className="font-sans reveal" fontSize={{
          base: '14px',
          md: '15px'
        }} color="#6E6E73" lineHeight="1.8" fontWeight="300" maxW="600px" mb={10} data-id="element-178">
            Gobernanza no es un comité. No es un documento. Es un ciclo de
            cuatro prácticas que cualquier equipo puede instalar esta semana. El
            objetivo no es decidir todo — es asegurarse de que lo que importa
            tenga dueño, criterio y revisión.
          </Text>

          <Flex direction={{
          base: 'column',
          lg: 'row'
        }} align={{
          base: 'center',
          lg: 'flex-start'
        }} gap={{
          base: 10,
          lg: 14
        }} mb={8} data-id="element-179">
            <Box flexShrink={0} className="reveal" w={{
            base: '100%',
            lg: '440px'
          }} data-id="element-180">
              <svg viewBox="0 0 440 440" style={{
              width: '100%',
              height: 'auto'
            }} data-id="element-181">
                <defs data-id="element-182">
                  <marker id="cycleArrow" markerWidth="7" markerHeight="6" refX="5" refY="3" orient="auto" data-id="element-183">
                    <polygon points="0 0, 7 3, 0 6" fill="#C7C7CC" data-id="element-184" />
                  </marker>
                </defs>
                {arcs.map((d, i) => <path key={i} d={d} fill="none" stroke="#D2D2D7" strokeWidth="1.5" markerEnd="url(#cycleArrow)" data-id="element-185" />)}
                <circle cx={cx} cy={cy} r="42" fill="#FFFFFF" stroke="#0071E3" strokeWidth="2" data-id="element-186" />
                <text x={cx} y={cy - 6} textAnchor="middle" fill="#0071E3" fontSize="8" fontFamily="'Courier New', monospace" letterSpacing="1.5" data-id="element-187">
                  DECISIÓN
                </text>
                <text x={cx} y={cy + 8} textAnchor="middle" fill="#0071E3" fontSize="8" fontFamily="'Courier New', monospace" letterSpacing="1.5" data-id="element-188">
                  EXPLÍCITA
                </text>
                {pts.map(node => <g key={node.label} data-id="element-189">
                    <circle cx={node.x} cy={node.y} r={nodeR} fill="#FFFFFF" stroke="#D2D2D7" strokeWidth="1" data-id="element-190" />
                    <circle cx={node.x + nodeR * 0.6} cy={node.y - nodeR * 0.6} r="12" fill="#0071E3" data-id="element-191" />
                    <text x={node.x + nodeR * 0.6} y={node.y - nodeR * 0.6 + 4} textAnchor="middle" fill="#FFFFFF" fontSize="8" fontFamily="'Courier New', monospace" fontWeight="700" data-id="element-192">
                      {node.num}
                    </text>
                    <text x={node.x} y={node.y + 5} textAnchor="middle" fill="#1D1D1F" fontSize="10" fontFamily="'Courier New', monospace" letterSpacing="2" fontWeight="600" data-id="element-193">
                      {node.label}
                    </text>
                  </g>)}
              </svg>
            </Box>

            <Box flex="1" w="full" data-id="element-194">
              <Text className="font-mono reveal" fontSize="10px" color="#86868B" letterSpacing="0.2em" textTransform="uppercase" mb={6} data-id="element-195">
                Los cuatro momentos
              </Text>
              <VStack spacing={0} align="stretch" data-id="element-196">
                {cycleNodes.map((node, i) => <Box key={node.label} py={6} borderBottom={i < cycleNodes.length - 1 ? '1px solid #F5F5F7' : 'none'} className={`reveal reveal-delay-${i}`} data-id="element-197">
                    <Flex align="baseline" gap={3} mb={2} data-id="element-198">
                      <Text className="font-mono" fontSize="10px" color="#86868B" fontWeight="600" data-id="element-199">
                        {node.num}
                      </Text>
                      <Text className="font-mono" fontSize="12px" color="#1D1D1F" fontWeight="600" letterSpacing="0.12em" data-id="element-200">
                        {node.label}
                      </Text>
                    </Flex>
                    <Text className="font-sans" fontSize="13px" color="#6E6E73" lineHeight="1.7" fontWeight="300" data-id="element-201">
                      {node.desc}
                    </Text>
                  </Box>)}
              </VStack>
            </Box>
          </Flex>

          <Box className="apple-card-gray reveal" p={{
          base: 8,
          md: 10
        }} borderLeft="3px solid #0071E3" borderRadius="0 12px 12px 0" data-id="element-202">
            <Text className="font-sans" fontSize={{
            base: '14px',
            md: '15px'
          }} color="#6E6E73" lineHeight="1.8" fontWeight="300" data-id="element-203">
              A diferencia del sistema que produce sin decidir — donde los
              componentes orbitan alrededor de un centro ausente — este ciclo
              llega al centro. La{' '}
              <Box as="span" color="#1D1D1F" data-id="element-204">
                decisión explícita
              </Box>{' '}
              no es el resultado del ciclo. Es su condición de partida.
            </Text>
          </Box>
        </Box>

        <ThinRule data-id="element-205" />

        {/* TOOL 03 */}
        <Box mb={16} data-id="element-206">
          <Flex align="center" gap={3} mb={3} data-id="element-207">
            <Box w="28px" h="28px" borderRadius="full" bg="#0071E3" display="flex" alignItems="center" justifyContent="center" flexShrink={0} data-id="element-208">
              <Text className="font-mono" fontSize="10px" color="#FFFFFF" fontWeight="700" data-id="element-209">
                03
              </Text>
            </Box>
            <Text className="font-mono reveal" fontSize="12px" color="#1D1D1F" letterSpacing="0.15em" textTransform="uppercase" fontWeight="600" data-id="element-210">
              La Cadencia Semanal
            </Text>
          </Flex>
          <Text className="font-serif-italic reveal" fontSize={{
          base: '15px',
          md: '17px'
        }} color="#6E6E73" mb={6} data-id="element-211">
            ¿Cuándo revisar lo que el sistema está decidiendo — sin agregar
            reuniones?
          </Text>
          <Text className="font-sans reveal" fontSize={{
          base: '14px',
          md: '15px'
        }} color="#6E6E73" lineHeight="1.8" fontWeight="300" maxW="600px" mb={10} data-id="element-212">
            Gobernar no es un proyecto con inicio y fin. Es una práctica
            semanal. Cuatro momentos en la semana que convierten la ambigüedad
            acumulada en aprendizaje explícito.
          </Text>

          <Grid templateColumns={{
          base: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(4, 1fr)'
        }} gap={4} data-id="element-213">
            {[{
            day: 'Lunes',
            title: 'Nombrar',
            text: '¿Qué decisiones vamos a tomar esta semana? ¿Cuáles ya están tomadas implícitamente y nadie las declaró? Dedicar 15 minutos a nombrar lo que el sistema está a punto de hacer.',
            accent: true
          }, {
            day: 'Martes — Jueves',
            title: 'Ejecutar',
            text: 'Con el criterio visible. Con responsable asignado. Con un registro mínimo del razonamiento detrás de cada decisión que importe.',
            accent: false
          }, {
            day: 'Viernes',
            title: 'Revisar',
            text: '¿Qué decidió el sistema esta semana que nadie declaró? ¿Qué consecuencias produjo que nadie diseñó? ¿Qué criterio necesita actualizarse antes de que vuelva a ejecutarse?',
            accent: false
          }, {
            day: 'Siguiente Lunes',
            title: 'Actualizar',
            text: 'El criterio que sobrevivió a la semana se consolida. El que no funcionó se corrige antes de escalar. El sistema aprende — en ciclos de cinco días, no en sprints de seis meses.',
            accent: false
          }].map((item, i) => <Box key={i} className={`apple-card card-hover reveal reveal-delay-${i}`} p={{
            base: 7,
            md: 8
          }} borderTop={item.accent ? '3px solid #0071E3' : '3px solid transparent'} data-id="element-214">
                <Text className="font-mono" fontSize="10px" color="#86868B" letterSpacing="0.15em" textTransform="uppercase" mb={3} data-id="element-215">
                  {item.day}
                </Text>
                <Text className="font-serif-italic" fontSize={{
              base: '18px',
              md: '20px'
            }} color="#1D1D1F" mb={4} data-id="element-216">
                  {item.title}
                </Text>
                <Text className="font-sans" fontSize="13px" color="#6E6E73" lineHeight="1.6" fontWeight="300" data-id="element-217">
                  {item.text}
                </Text>
              </Box>)}
          </Grid>
        </Box>

        {/* Paperback 3D mockup */}
        <Box className="reveal" textAlign="center" mb={16} data-id="element-218">
          <Box as="img" src="https://cdn.magicpatterns.com/uploads/5zhGbzQyiXArd3vxyzeWLx/mockup-of-a-paperback-book-standing-over-another-one-3440-el1.png" alt="La urgencia de decidir — libro físico" w={{
          base: '280px',
          md: '380px'
        }} mx="auto" display="block" style={{
          objectFit: 'contain',
          filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.12))'
        }} data-id="element-219" />
        </Box>

        <Box textAlign="center" className="reveal" data-id="element-220">
          <Text className="font-serif-italic" fontSize={{
          base: '20px',
          md: '28px'
        }} color="#1D1D1F" lineHeight="1.5" maxW="600px" mx="auto" data-id="element-221">
            Gobernar no es un proyecto.
            <br data-id="element-222" />
            Es una práctica semanal que convierte la ambigüedad en aprendizaje.
          </Text>
        </Box>
      </Box>
    </Box>;
}
// ── Section 06: LAS REFLEXIONES ───────────────────────────────────
function Section06Reflections() {
  return <Box as="section" bg="#FFFFFF" px={{
    base: 6,
    md: 12,
    lg: 20
  }} py={{
    base: 20,
    md: 36
  }} data-id="element-223">
      <Box maxW="580px" mx="auto" textAlign="center" data-id="element-224">
        <SectionLabel number="06" title="Las reflexiones" data-id="element-225" />

        <Text className="font-serif-italic reveal" fontSize={{
        base: '24px',
        md: '36px'
      }} color="#1D1D1F" lineHeight="1.25" letterSpacing="-0.01em" mb={4} data-id="element-226">
          Hay una parte de mí que quisiera terminar esto como terminan los
          libros que se sienten seguros.
        </Text>
        <Text className="font-sans reveal" fontSize={{
        base: '17px',
        md: '19px'
      }} color="#6E6E73" lineHeight="1.5" fontWeight="300" mb={10} data-id="element-227">
          Con una conclusión limpia, una promesa y un camino. Pero no puedo.
        </Text>

        <Text className="font-sans reveal" fontSize={{
        base: '15px',
        md: '17px'
      }} color="#6E6E73" lineHeight="1.8" fontWeight="300" textAlign="left" mb={10} data-id="element-228">
          No porque me falte convicción. Sino porque lo que vi en estos años no
          se deja domesticar tan fácil. Cuando uno trabaja suficiente tiempo con
          organizaciones grandes — bancos que mueven el pulso del país,
          aseguradoras que venden tranquilidad como producto, operaciones que
          sostienen la calle con logística y terreno — aprende que la verdad
          rara vez viene con bordes claros.
        </Text>

        <ThinRule data-id="element-229" />

        <Text className="font-serif reveal" fontSize={{
        base: '20px',
        md: '26px'
      }} color="#1D1D1F" lineHeight="1.4" mb={8} data-id="element-230">
          Tengo dudas. Dudas reales.
        </Text>

        <Text className="font-sans reveal" fontSize={{
        base: '15px',
        md: '17px'
      }} color="#6E6E73" lineHeight="1.8" fontWeight="300" textAlign="left" mb={6} data-id="element-231">
          La primera es la más incómoda: no sé si nombrar el problema lo
          resuelve. He visto organizaciones que nombran muy bien sus
          disfunciones y las reproducen con notable consistencia de todas
          formas. Nombrar es necesario. No es suficiente.
        </Text>
        <Text className="font-sans reveal" fontSize={{
        base: '15px',
        md: '17px'
      }} color="#6E6E73" lineHeight="1.8" fontWeight="300" textAlign="left" mb={10} data-id="element-232">
          La segunda es más íntima: la sensación de llegar tarde. Tarde para
          influir. Tarde para evitar el error. Tarde para corregir sin costo.
          Hay proyectos donde uno entra y ya huele que el destino está escrito.
          No por fatalismo, sino por estructura: el roadmap cerrado, la
          integración comprometida, el presupuesto gastado, el sponsor buscando
          confirmación y no verdad.
        </Text>

        <ThinRule data-id="element-233" />

        <Box className="pull-quote-border reveal" textAlign="left" mb={10} data-id="element-234">
          <Text className="font-serif-italic" fontSize={{
          base: '18px',
          md: '22px'
        }} color="#1D1D1F" lineHeight="1.6" data-id="element-235">
            "Si sientes que algo en el sistema cambió y no puedes nombrarlo del
            todo — este libro fue escrito para ti."
          </Text>
        </Box>

        <Text className="font-sans reveal" fontSize={{
        base: '15px',
        md: '17px'
      }} color="#6E6E73" lineHeight="1.8" fontWeight="300" textAlign="left" mb={14} data-id="element-236">
          No para darte respuestas limpias. Para darte la confirmación de que lo
          que sientes tiene nombre. Y para que ese nombre sea el punto de
          partida — no el punto de llegada.
        </Text>

        {/* Woman reading */}
        <Box className="reveal" mb={14} borderRadius="18px" overflow="hidden" w="full" data-id="element-237">
          <Box as="img" src="https://cdn.magicpatterns.com/uploads/r6sRnmVEGJrveBeP23j3p1/pretty-black-girl-reading-a-book-mockup-alone-a17330.png" alt="Lectora con La urgencia de decidir" w="full" style={{
          objectFit: 'cover',
          maxHeight: '420px',
          display: 'block'
        }} data-id="element-238" />
        </Box>

        {/* Signature */}
        <Box mb={12} className="reveal" data-id="element-239">
          <Text className="font-mono" fontSize="12px" letterSpacing="0.25em" color="#1D1D1F" textTransform="uppercase" mb={2} data-id="element-240">
            Gustavo Soto Miño
          </Text>
          <Text className="font-serif-italic" fontSize="14px" color="#86868B" data-id="element-241">
            Santiago de Chile, 2026
          </Text>
        </Box>

        <Box className="section-rule reveal" mb={10} data-id="element-242" />

        {/* CTA */}
        <Flex direction="column" align="center" gap={2} mb={12} className="reveal" data-id="element-243">
          <Button as="a" href="https://a.co/d/042bHcr3" target="_blank" rel="noopener noreferrer" bg="#0071E3" color="#FFFFFF" borderRadius="980px" px={8} py={6} h="auto" fontSize="14px" fontFamily="'DM Sans', sans-serif" letterSpacing="0.01em" fontWeight="400" _hover={{
          bg: '#0077ED'
        }} transition="all 0.2s ease" data-id="element-244">
            Conseguir el libro en Amazon →
          </Button>
        </Flex>

        <Text className="font-mono" fontSize="10px" color="#86868B" letterSpacing="0.15em" data-id="element-245">
          La urgencia de decidir · Gustavo Soto Miño · 2026
        </Text>
      </Box>
    </Box>;
}
// ── Main Page ─────────────────────────────────────────────────────
export function ScrollPage() {
  useScrollReveal();
  useEffect(() => {
    document.title = 'La urgencia de decidir - Gustavo Soto Miño';
  }, []);
  return <Box bg="#FFFFFF" minH="100vh" w="full" data-id="element-246">
      <Section00Cover data-id="element-247" />
      <Section01Message data-id="element-248" />
      <Section02Context data-id="element-249" />
      <Section03Evidence data-id="element-250" />
      <Section04Urgency data-id="element-251" />
      <Section05Paths data-id="element-252" />
      <Section06Reflections data-id="element-253" />
    </Box>;
}